import React, { useEffect, useState } from 'react'
import { Badge } from 'components/badge'
import { Divider } from 'components/divider'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Input, InputGroup } from 'components/input'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { useMutation } from '@apollo/client'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { RoutesIcon } from 'components/icons'
import {
    ArrowLeftIcon,
    MagnifyingGlassIcon,
    EllipsisVerticalIcon,
    EyeIcon,
    PencilIcon,
    ArrowPathIcon,
} from '@heroicons/react/20/solid'
import { useNavigate } from 'react-router-dom'
import { RouteStatusDropdown } from 'components/custom/RouteStatusDropdown'
import { useLazyQuery } from '@apollo/client'
import { GET_MASTER_ROUTE_LIST } from 'graphql/route/queries'
import { REFRESH_ROUTE_SEAT_ASSIGNMENT } from 'graphql/route/mutations'
import { t } from 'i18next'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import loadingImage from '../../assets/images/boat-loading.gif'
import { GraphQlResponseNoData } from 'components/custom/GraphQlResponseDisplay'
import { getPageRange } from 'utils/paginationHelper'

export default function MetroRoutesMaster() {
    const navigate = useNavigate()
    const [searchString, setSearchString] = useState('')
    const [searchStatus, setSearchStatus] = useState('All')

    // Dialog Message
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    //
    const [routeList, setRouteList] = useState(null)
    const [routeCount, setRouteCount] = useState(0)

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(routeCount / rowsPerPage)

    const [getRouteList, { loading: routeListLoading, error: routeListError }] = useLazyQuery(GET_MASTER_ROUTE_LIST, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data.getMasterBoatRoute.success && data.getMasterBoatRoute.code === '200') {
                setRouteList(data.getMasterBoatRoute.master_boat_routes)
                setRouteCount(data.getMasterBoatRoute.total_count)
            }
        },
    })
    useEffect(() => {
        console.log('selected page===', page)
        searchBoatsRoutes(searchString, searchStatus)
    }, [page, rowsPerPage])
    const handlePageChange = newPage => {
        console.log('new handlePageChange===', newPage)
        setPage(newPage)
    }
    // Search Route  List
    const searchBoatsRoutes = (searchKey, status) => {
        getRouteList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                routeId: null,
                routeName: searchKey === '' ? null : searchKey,
                statusCode: status === 'All' ? null : searchStatus,
            },
        })
    }

    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        searchBoatsRoutes(searchString, searchStatus)
    }
    const onClickSearchClear = () => {
        console.log('searchString before clear:', searchString)
        setSearchString('') // Clear the search string
        setSearchStatus('All')
        setPage(0)
        setRowsPerPage(10)
        searchBoatsRoutes('', 'All') // Call searchBoats with an empty string after clearing
        // Let state update before invoking
    }
    const handleErrClose = () => {
        setShowErrDialog(false)
        setErrorMessage('')
    }
    const handleRouteStatusChange = e => {
        console.log('selected status===', e.target.value)
        setSearchStatus(e.target.value)
    }

    //Mutation Update Captain Shift Schedule
    const [refreshRouteSeatAssignment] = useMutation(REFRESH_ROUTE_SEAT_ASSIGNMENT, {
        onCompleted: data => {
            if (data.refreshRouteSeatAssignment.success) {
                setShowSucDialog(true)
                setSuccessMessage(data.refreshRouteSeatAssignment.message)
                setTimeout(() => {
                    setShowSucDialog(false)
                }, 2000)
            } else {
                setErrorMessage(data.refreshRouteSeatAssignment.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setErrorMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const handleRefreshRouteSeatAssignment = () => {
        refreshRouteSeatAssignment({
            variables: {},
        })
    }
    return (
        <>
            <>
                <div className="mb-6 flex justify-between items-center">
                    <button
                        onClick={() => {
                            navigate('/')
                        }}
                        type="button"
                        className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                    >
                        <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                        {t('Home')}
                    </button>
                </div>

                <div className="mb-0 flex justify-between items-center">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                        <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                <RoutesIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                            </div>
                            <div>
                                <span className="ms-3"> {t('Manage Routes')}</span>
                                <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                    {t('Plan and monitor routes to ensure smooth operations.')}
                                </p>
                            </div>
                        </h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                        <button
                            onClick={() => navigate('/metro-master-routes/metro-route-master-form')}
                            type="button"
                            className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        >
                            {t('Add Route')}
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="max-sm:w-full sm:flex-1">
                        <div className="mt-8 mb-4 flex flex-col sm:flex-row  max-w-xl gap-4">
                            <div>
                                <label htmlFor="country" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Status')}
                                </label>
                                <RouteStatusDropdown
                                    fields={{
                                        name: 'roteStatus',
                                        value: searchStatus,
                                        onChange: handleRouteStatusChange,
                                    }}
                                    error=""
                                    helperText=""
                                    purpose="filter"
                                />
                            </div>
                            <div className="max-w-xl">
                                <label htmlFor="country" className="block text-sm font-medium leading-6 text-gray-900">
                                    {t('Search')}
                                </label>
                                <InputGroup>
                                    <MagnifyingGlassIcon />
                                    <Input
                                        id="search"
                                        value={searchString}
                                        onChange={e => setSearchString(e.target.value)}
                                        placeholder="Search routes"
                                    />
                                </InputGroup>
                            </div>
                            <div className="flex-none grow space-x-2 justify-end place-content-end">
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.stopPropagation() // Prevent the click event from bubbling up
                                        onClickSearch()
                                    }}
                                    className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                                >
                                    {t('Search')}
                                </button>
                                <button
                                    type="button"
                                    onClick={e => {
                                        e.stopPropagation() // Prevent the click event from bubbling up
                                        onClickSearchClear()
                                    }}
                                    className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                >
                                    {t('Clear')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="mt-4 mb-4 flex flex-col sm:flex-row  max-w-xl gap-4">
                            <button
                                type="button"
                                onClick={e => {
                                    e.stopPropagation() // Prevent the click event from bubbling up
                                    handleRefreshRouteSeatAssignment()
                                }}
                                className="flex items-center bg-blue-500 text-primary font-bold py-2 px-4 rounded hover:bg-blue-700"
                            >
                                <ArrowPathIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 me-2" />
                                {t('Refresh')}
                            </button>
                        </div>
                    </div>
                </div>
                <Divider />
            </>
            {routeListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {routeListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch routes!')}</div>
                </div>
            )}
            {!routeListLoading && !routeListError && routeList?.length === 0 && <GraphQlResponseNoData />}
            {!routeListLoading && !routeListError && routeList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    {/* <TableHeader>
                                        {t('S.No')}
                                    </TableHeader> */}
                                    <TableHeader>{t('Route Name')}</TableHeader>
                                    <TableHeader>{t('Route Type')}</TableHeader>
                                    <TableHeader>{t('Starts at')}</TableHeader>
                                    <TableHeader>{t('Ends at')}</TableHeader>
                                    <TableHeader>{t('Waypoints')}</TableHeader>
                                    <TableHeader>{t('Status')}</TableHeader>
                                    <TableHeader className="text-right">{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {routeList.map(route => (
                                    <TableRow
                                        className="cursor-pointer hover:bg-gray-50"
                                        key={route.master_route_id}
                                        onClick={() =>
                                            navigate(
                                                `/metro-routes/metro-route-master-details/${route.master_route_id}`
                                            )
                                        }
                                        title={`Route #${route.master_route_id}`}
                                    >
                                        {/* <TableCell>01</TableCell> */}
                                        <TableCell>{route.route_name.en}</TableCell>
                                        <TableCell>
                                            {route.route_type?.description?.en || route.route_type?.code || '-'}
                                        </TableCell>
                                        <TableCell>{route.waypoints[0]?.location?.name?.en}</TableCell>

                                        <TableCell>
                                            {route.waypoints[route.waypoints.length - 1]?.location?.name?.en}
                                        </TableCell>

                                        <TableCell>{route.waypoints.length}</TableCell>
                                        <TableCell>
                                            <Badge
                                                color={
                                                    route.status.code === 'ACT'
                                                        ? 'emerald'
                                                        : route.status.code === 'INA'
                                                        ? 'red'
                                                        : route.status.code === 'SUS'
                                                        ? 'zinc'
                                                        : 'rose'
                                                }
                                            >
                                                {route.status.description.en}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {' '}
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(
                                                                `/metro-routes/metro-route-master-details/${route.master_route_id}`
                                                            )
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(
                                                                `/metro-master-routes/metro-route-master-form/${route.master_route_id}`
                                                            )
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}

            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={errorMessage} onClose={handleErrClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={successMessage} onClose={handleErrClose} />
            )}
        </>
    )
}
